import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useLanguage } from "@/context/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, CalendarPlus } from "lucide-react";
import { googleCalendarUrl } from "@/lib/calendar";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [booking, setBooking] = useState(null);
  const polled = useRef(false);

  useEffect(() => {
    if (polled.current) return;
    polled.current = true;
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (data.booking_id) {
            api.get(`/bookings/${data.booking_id}`).then(({ data: b }) => setBooking(b)).catch(() => {});
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
      } catch (e) {
        console.error("Payment status poll failed:", e);
      }
      if (attempts >= 6) {
        setStatus("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 pt-40 pb-24 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-white mx-auto mb-8" />
            <h1 className="font-serif font-light text-4xl text-white">{t("success.confirming")}</h1>
            <p className="text-muted-foreground mt-4">{t("success.wait")}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="h-20 w-20 rounded-full border border-white/20 bg-white/10 flex items-center justify-center mx-auto mb-8">
              <CheckCircle2 className="h-10 w-10 text-white" />
            </div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl text-white" data-testid="payment-success-title">
              {t("success.confirmed")}
            </h1>
            <p className="text-muted-foreground mt-5 max-w-md mx-auto leading-relaxed">
              {t("success.confirmedNote")}
            </p>
            <div className="flex flex-wrap gap-4 justify-center mt-10">
              {booking && (
                <a href={googleCalendarUrl(booking)} target="_blank" rel="noopener noreferrer" data-testid="success-add-to-calendar-link">
                  <Button className="rounded-full bg-white text-black hover:bg-white/85 px-8">
                    <CalendarPlus className="h-4 w-4 mr-2" /> {t("detail.addCalendar")}
                  </Button>
                </a>
              )}
              <Button variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 px-8"
                onClick={() => navigate("/account")}>{t("success.myReservations")}</Button>
              <Button variant="ghost" className="rounded-full text-muted-foreground hover:text-white hover:bg-white/5 px-8"
                onClick={() => navigate("/")}>{t("success.home")}</Button>
            </div>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="h-20 w-20 rounded-full border border-white/20 bg-white/5 flex items-center justify-center mx-auto mb-8">
              <XCircle className="h-10 w-10 text-white/70" />
            </div>
            <h1 className="font-serif font-light text-4xl text-white">{t("success.failed")}</h1>
            <p className="text-muted-foreground mt-4">{t("success.failedNote")}</p>
            <Button className="rounded-full bg-white text-black hover:bg-white/85 px-8 mt-10"
              onClick={() => navigate("/account")}>{t("success.viewReservations")}</Button>
          </>
        )}
      </div>
    </div>
  );
}
